/**
 * Ce que les OCTETS d'une image disent d'elle : son type et sa taille.
 *
 * Une image que l'agent pose dans un acte vient d'une pièce du dossier, d'un
 * fichier joint à la conversation ou d'une image déjà dans l'acte (#2141). Le
 * type MIME déclaré et l'extension sont des dires : un PDF renommé `.png`
 * partirait à l'éditeur, qui n'accepte qu'un PNG ou un JPEG. Le backend (avant
 * tout envoi au navigateur) et le navigateur (pour une image copiée de l'acte)
 * jugent donc sur la signature des octets, par ce module — une seule lecture,
 * des deux côtés.
 */

/**
 * Poids maximal d'une image posée dans un acte : celui des en-têtes de cabinet
 * (`LETTERHEAD_MAX_BYTES`, `apps/backend/routes/api.routes.ts`). Une signature
 * ou un tampon scanné tient très en dessous ; au-delà, c'est une photographie
 * à réduire avant de la poser.
 */
export const CANVAS_IMAGE_SOURCE_MAX_BYTES = 2 * 1024 * 1024;

/** Les deux types que l'éditeur sait poser et que Word relit partout. */
export const CANVAS_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg'] as const;
export type CanvasImageMimeType = (typeof CANVAS_IMAGE_MIME_TYPES)[number];

/** Le type lu dans les octets : un type posable, ou le nom de ce que c'est à la place. */
export type CanvasImageByteKind =
  | { readonly posable: true; readonly mimeType: CanvasImageMimeType }
  | { readonly posable: false; readonly nom: string };

const commencePar = (octets: Uint8Array, signature: readonly number[], depuis = 0): boolean =>
  octets.length >= depuis + signature.length && signature.every((octet, rang) => octets[depuis + rang] === octet);

const ascii = (texte: string): number[] => [...texte].map((caractere) => caractere.charCodeAt(0));

/** Signatures des fichiers qu'un avocat dépose à la place d'un PNG ou d'un JPEG, pour les nommer dans le refus. */
const AUTRES_SIGNATURES: ReadonlyArray<{ nom: string; signature: readonly number[]; depuis?: number }> = [
  { nom: 'PDF', signature: ascii('%PDF') },
  { nom: 'GIF', signature: ascii('GIF8') },
  { nom: 'WebP', signature: ascii('WEBP'), depuis: 8 },
  { nom: 'BMP', signature: ascii('BM') },
  { nom: 'TIFF', signature: [0x49, 0x49, 0x2a, 0x00] },
  { nom: 'TIFF', signature: [0x4d, 0x4d, 0x00, 0x2a] },
  { nom: 'EMF', signature: ascii(' EMF'), depuis: 40 },
  { nom: 'HEIC', signature: ascii('ftyphei'), depuis: 4 },
  { nom: 'Word or zip archive', signature: [0x50, 0x4b, 0x03, 0x04] },
];

/**
 * Le type d'une image d'après ses premiers octets.
 *
 * @returns `posable:true` pour un PNG ou un JPEG ; sinon le nom du format
 *   reconnu (`PDF`, `GIF`, `SVG`…) ou `unknown format`, à citer dans le refus.
 */
export function typeDImageDesOctets(octets: Uint8Array): CanvasImageByteKind {
  if (commencePar(octets, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return { posable: true, mimeType: 'image/png' };
  if (commencePar(octets, [0xff, 0xd8, 0xff])) return { posable: true, mimeType: 'image/jpeg' };
  const autre = AUTRES_SIGNATURES.find(({ signature, depuis }) => commencePar(octets, signature, depuis));
  if (autre !== undefined) return { posable: false, nom: autre.nom };
  // Un SVG est du texte : sa balise peut suivre un prologue XML et un commentaire.
  const debut = String.fromCharCode(...octets.subarray(0, 512)).toLowerCase();
  if (debut.includes('<svg')) return { posable: false, nom: 'SVG' };
  return { posable: false, nom: 'unknown format' };
}

const entier32 = (octets: Uint8Array, rang: number): number =>
  ((octets[rang]! << 24) | (octets[rang + 1]! << 16) | (octets[rang + 2]! << 8) | octets[rang + 3]!) >>> 0;
const entier16 = (octets: Uint8Array, rang: number): number => (octets[rang]! << 8) | octets[rang + 1]!;

/**
 * La taille en pixels inscrite dans un PNG (son `IHDR`) ou un JPEG (son
 * segment de trame `SOFn`).
 *
 * L'orientation EXIF d'un JPEG n'est pas lue : une photographie prise
 * téléphone tourné rend ici ses dimensions de capteur.
 *
 * @returns `undefined` quand les octets ne portent pas de taille lisible
 *   (fichier tronqué, côté nul) : l'appelant refuse plutôt que de poser une
 *   image aux proportions inventées.
 */
export function tailleEnPixelsDeLImage(octets: Uint8Array, mimeType: CanvasImageMimeType): { width: number; height: number } | undefined {
  const rendre = (width: number, height: number): { width: number; height: number } | undefined => (width > 0 && height > 0 ? { width, height } : undefined);
  if (mimeType === 'image/png') {
    // Signature (8) + longueur d'IHDR (4) + « IHDR » (4), puis largeur et hauteur sur 4 octets chacune.
    if (octets.length < 24 || !commencePar(octets, ascii('IHDR'), 12)) return undefined;
    return rendre(entier32(octets, 16), entier32(octets, 20));
  }
  let rang = 2;
  while (rang + 9 < octets.length) {
    if (octets[rang] !== 0xff) return undefined;
    const marque = octets[rang + 1]!;
    // Octets de remplissage et marques sans longueur (RSTn, SOI, TEM).
    if (marque === 0xff) { rang += 1; continue; }
    if ((marque >= 0xd0 && marque <= 0xd8) || marque === 0x01) { rang += 2; continue; }
    // SOF0 à SOF15, sauf DHT (C4), JPG (C8) et DAC (CC) qui partagent la plage.
    if (marque >= 0xc0 && marque <= 0xcf && marque !== 0xc4 && marque !== 0xc8 && marque !== 0xcc) {
      return rendre(entier16(octets, rang + 7), entier16(octets, rang + 5));
    }
    const longueur = entier16(octets, rang + 2);
    if (longueur < 2) return undefined;
    rang += 2 + longueur;
  }
  return undefined;
}
